/**
 * The bubble's hover actions — Figma's `ButtonGroup`.
 *
 * The add-reaction button opens it, then the actions the message offers directly, then a
 * "More" menu for the rest. It floats over the bubble's top edge, which is where it has to
 * go now that the reaction row occupies the bottom one.
 *
 * ## It does not mirror
 *
 * The bar is right-aligned against the bubble for every message, and reads left to right in
 * both. An own message used to anchor it to the other corner; one layout is one thing to
 * learn, and the reaction row below it made the same change.
 *
 * The one exception is an incoming bubble narrower than the bar ("Hi"). Right-aligned, the
 * bar would grow left past the avatar and be clipped by the panel edge, so it starts at the
 * bubble's left edge instead and grows into the empty space on the right. CSS's `safe`
 * alignment makes that call, so no measuring is involved. An own message never takes it:
 * the empty space is on ITS left, which plain right-alignment already grows into, while
 * the fallback would push the bar off the panel's right edge.
 *
 * ## Which icons it draws
 *
 * Whichever the policy put in `actions.bar`, in that order, and a "More" trigger whenever
 * `actions.menu` has anything in it. The bar does not decide: it used to take one flat list
 * and slice it at a limit, so a control changed position as a SIBLING appeared or dropped
 * out. `conversation/_libs/message-actions.ts` owns the split now, and this component
 * renders it.
 *
 * The add-reaction button is not one of those actions. It is a picker rather than a
 * command, and it opens the bar on every message the viewer may react to — including one
 * that already has reactions, which also carries a copy of the button at the end of its
 * reaction row (`channel-message-reaction-footer`). A control that moved once the first
 * reaction landed was a control the reader had to re-find.
 *
 * ## It must be `invisible`, never `hidden`
 *
 * Every button in here is a tooltip trigger, and a trigger with `display: none` has a 0×0
 * box at the document origin — so a tooltip still closing when the pointer leaves flies to
 * the top-left corner of the page. Holding the box costs nothing because the bar is
 * absolutely positioned: it is out of flow either way.
 *
 * The reveal comes from the ancestor `group` rather than from the bar's own `:hover`, and
 * it has to: a `visibility: hidden` element receives no pointer events, so `hover:visible`
 * on this box could never match. `MESSAGE_HOVER_VISIBLE` carries the other half — an open
 * tooltip keeps the bar up while the pointer is away on it.
 *
 * Three things hold the bar up, then: the pointer on the message, focus inside it, and an
 * overlay this bar opened — a tooltip, the "More" menu, or the reaction picker. The pointer
 * leaves the message the moment a menu opens under it, so without that last one the bar
 * disappeared on the very click that opened its own menu. The class list below says which
 * attribute each one reads, and why the menu's is `aria-expanded` and not `data-state`.
 *
 * ## A finished action takes it back down
 *
 * `dismissed` outranks all three: once a control has COMPLETED something — an icon clicked,
 * a "More" row picked, an emoji chosen — the bar has nothing left to offer, and a bar still
 * hanging over the bubble is chrome the reader has to look past. Focus is what would
 * otherwise keep it there: the click that ran the action also focused the button it ran
 * from. The dismissal lasts until the pointer leaves the message; `channel-message-chrome`
 * owns that state, because the reset reads the bubble's `pointerleave` and this bar floats
 * outside the bubble's box.
 *
 * The two controls that OPEN an overlay — the add-reaction button and the "More" trigger —
 * do not dismiss. They have not finished anything yet; the pick and the row that follow do.
 *
 * That group is `BubbleBox`, which is the bubble's own width and nothing more. It used to
 * be `BubbleColumn`, the message's full width BUDGET, so a pointer in the empty space
 * beside a one-word bubble — half the thread, on a wide panel — revealed a bar anchored to
 * a bubble it was nowhere near. The bar itself is a descendant of the box, so hovering the
 * bar still counts as hovering the box even though it floats outside the box's edges.
 */

import { cn } from "cn";
import { kebabCase } from "lodash-es";

import { Button } from "@/components/ui/button/button";
import { BwEmojiSmilingAddLine } from "@/components/ui/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MESSAGE_HOVER_VISIBLE } from "../_libs/message-hover";
import type { ChannelMessageActionGroups } from "../_types/channel-surface";
import { ChannelMessageMoreMenu } from "./channel-message-more-menu";
import { ReactionPickerPopover } from "./channel-message-reaction-picker";

/**
 * Figma's `ButtonIcon`: 32px square, round, transparent until hovered.
 *
 * The glyph is NOT sized here, and that is the point: at 24px the button had no room for
 * the `Button` base's `[&_svg:not([class*='size-'])]:size-5`, so every icon in the bar
 * carried its own `size-4` to opt out. A 32px button fits the 20px glyph, which is the
 * pairing the rest of the app uses for `icon-md` — so the overrides are gone and the base
 * sizes every icon, the "More" trigger and the add-reaction button alike.
 */
const BAR_BUTTON = cn(
  /* sorted */
  "rounded-full bg-bg-transparent motion-reduce:transition-none",
  "transition-[color,box-shadow,scale] duration-100",
  "hover:bg-bg-dark-min active:scale-95",
);

type ChannelMessageActionBarProps = {
  /**
   * Which side the bubble sits on. Only an incoming (`start`) bar may fall back to the
   * bubble's left edge when the bubble is narrower than the bar.
   */
  align: "start" | "end";
  /** Already split into the bar's icons and the "More" menu's rows. */
  actions: ChannelMessageActionGroups;
  /**
   * When set, the bar opens with an add-reaction button that picks through this. Omitted
   * only where a reaction would not be accepted — a closed channel, a type that
   * offers no reactions, a message not on the server yet (`channel-message-chrome`).
   */
  onPickReaction?: (type: string) => void;
  /**
   * True once a control here has finished something, until the pointer leaves the message.
   * Owned by `channel-message-chrome`, which is where the reset can be read.
   */
  dismissed: boolean;
  /** Raised by every control that COMPLETES something — never by one that opens an overlay. */
  onDismiss: () => void;
};

export function ChannelMessageActionBar({
  align,
  actions,
  onPickReaction,
  dismissed,
  onDismiss,
}: ChannelMessageActionBarProps) {
  const { bar, menu } = actions;

  if (bar.length === 0 && menu.length === 0 && !onPickReaction) return null;

  return (
    // The positioner spans the bubble so the bar can be ALIGNED inside it rather than pinned
    // to one corner — see the module doc for when an incoming bar falls back to the left.
    // It never takes the pointer: it covers the bubble's top edge even while the bar is
    // hidden, and the bar turns pointer events back on for itself.
    <div
      className={cn(
        /* sorted */
        // `-top-6` holds the bar's BOTTOM edge where it has always been: 34px of bar (32px
        // buttons plus the 1px border on each side) starting 24px up ends 10px into the
        // bubble, which is exactly where the 26px bar starting 16px up ended. Growing the
        // buttons therefore moves nothing the reader can see — the 8px goes off the TOP,
        // into the gutter above the bubble, rather than down over the first line of the
        // body. It clears the author's name on the way: that header is left-aligned and
        // this bar is right-aligned, and an own message has no header at all.
        "absolute -top-6 inset-x-0 z-20",
        "flex",
        "pointer-events-none",
        align === "start" ? "justify-end-safe" : "justify-end",
      )}
    >
      <div
        data-test="message-action-bar"
        className={cn(
          /* sorted */
          // `shrink-0`: the bar is wider than a short bubble on purpose, and must overflow
          // the positioner rather than squeeze its buttons.
          "flex items-center gap-0.5 shrink-0",
          "pointer-events-auto",
          "bg-bg-surface-elevate-low border border-border-dark-low rounded-full shadow-sm",
          // `visibility` is a discrete but transitionable property, so pairing it with
          // opacity fades the bar in AND out while it always has a box to anchor a tooltip
          // to. It has to stay AHEAD of the `visible` states below — same conflict family,
          // last one wins. See the module doc for why `hidden` is not an option.
          "invisible opacity-0",
          "transition-[opacity,visibility] duration-100",
          "motion-reduce:transition-none",
          // Nothing reveals the bar once one of its controls has finished something — the
          // rules are dropped rather than overridden, because every one of them outranks a
          // plain `invisible` on specificity. `visibility: hidden` takes the bar's pointer
          // events with it, so the bubble under it stays reachable through the gap.
          !dismissed &&
            cn(
              /* sorted */
              // Hover and focus are the same rule on the same box: whichever way the reader
              // reached this message, its controls are up. Focus is what carries the moment a
              // menu closes — Radix hands focus back to the trigger, and without this the bar
              // it sits in would vanish out from under it whenever the pointer had moved on.
              "group-hover:visible group-hover:opacity-100",
              "group-focus-within:visible group-focus-within:opacity-100",
              // An open menu or picker holds the bar up, and `aria-expanded` is what says so.
              // `data-state` cannot: both of those triggers are ALSO a tooltip trigger, and
              // with two `asChild` triggers on one button the outer one's props are spread
              // last by the inner one (`{ "data-state": open ? … , ...triggerProps }` in
              // Radix's trigger) — so the tooltip's `closed` overwrites the menu's `open` and
              // this never matched. The tooltip sets no `aria-expanded`, so that attribute
              // survives the merge intact.
              "has-aria-expanded:visible has-aria-expanded:opacity-100",
              MESSAGE_HOVER_VISIBLE,
            ),
        )}
      >
        {onPickReaction && (
          // The tooltip is the popover's, not this button's: the trigger has to be the
          // INNERMOST of the two wrappers (`TooltipTrigger` > `PopoverTrigger` > `Button`),
          // and the popover is what owns that nesting — see `ReactionPickerPopover`.
          <ReactionPickerPopover
            align="end"
            tooltip="Add reaction"
            // The button opens the picker and finishes nothing; the PICK is what finishes.
            // `ReactionPickerPopover` closes itself before it calls back, so by the time the
            // bar goes down the picker is already out of the tree.
            onPick={(type) => {
              onPickReaction(type);
              onDismiss();
            }}
          >
            <Button
              kind="ghost"
              variant="neutral"
              size="icon-md"
              aria-label="Add reaction"
              data-test="message-reaction-add"
              className={BAR_BUTTON}
            >
              <BwEmojiSmilingAddLine />
            </Button>
          </ReactionPickerPopover>
        )}

        {bar.map((action) => (
          <Tooltip key={kebabCase(action.title)}>
            <TooltipTrigger asChild>
              <Button
                size="icon-md"
                kind="ghost"
                variant="neutral"
                className={cn(
                  BAR_BUTTON,
                  action.variant === "destructive" && "text-text-negative",
                )}
                data-test={kebabCase(action.title)}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  action.onClick(event);
                  // `detail` is 0 on a keyboard-activated click. Retiring the bar there would
                  // hide it with focus still on this button, and a `visibility: hidden`
                  // element cannot hold focus — the browser drops it to the body and the
                  // reader loses their place in the tab order. A pointer click has nowhere to
                  // lose.
                  if (event.detail > 0) onDismiss();
                }}
              >
                <action.icon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{action.title}</TooltipContent>
          </Tooltip>
        ))}

        <ChannelMessageMoreMenu
          items={menu}
          className={BAR_BUTTON}
          onSelectItem={onDismiss}
        />
      </div>
    </div>
  );
}
